import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/usePageTitle";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  usePageTitle("Choose a new password — Olivia Does");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: form.password });
      toast.success(data.message || "Password reset.");
      navigate("/login");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md od-card p-8">
        <h1 className="font-display text-3xl">Choose a new password</h1>
        {!token ? (
          <div data-testid="reset-no-token" className="mt-6 rounded-lg border border-[#993333]/30 bg-[#993333]/10 text-[#993333] text-sm px-4 py-3">
            This link is missing its reset token. Please use the link from your email, or <Link to="/forgot-password" className="underline">request a new one</Link>.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            {error && (
              <div data-testid="reset-error" className="rounded-lg border border-[#993333]/30 bg-[#993333]/10 text-[#993333] text-sm px-4 py-3">{error}</div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#78716C]">New password</Label>
              <Input data-testid="reset-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 rounded-xl border-[#E2DBD6]" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#78716C]">Confirm password</Label>
              <Input data-testid="reset-confirm" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-2 rounded-xl border-[#E2DBD6]" />
            </div>
            <Button data-testid="reset-submit" disabled={busy} type="submit" className="w-full rounded-full bg-[#2A2624] hover:bg-[#1E1A18] py-6">
              {busy ? "Saving…" : "Reset password"}
            </Button>
          </form>
        )}
        <p className="text-sm text-[#78716C] mt-6"><Link to="/login" className="od-link font-medium">Back to sign in</Link></p>
      </div>
    </div>
  );
}
